import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { CostCalculationExternalSurchargeSetting } from 'generated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';

import { ControlledSelect, Drawer, showSnackbar } from 'components/shared';
import ControlledFunctionSelector from 'components/shared/ControlledFields/ControlledFunctionSelector';
import ControlledMonetaryField from 'components/shared/ControlledFields/ControlledMonetaryField';
import ControlledPercentageField from 'components/shared/ControlledFields/ControlledPercentageField';
import ControlledRadioGroup from 'components/shared/ControlledFields/ControlledRadioGroup';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import ValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import DrawerFooter from 'components/shared/Drawer/DrawerFooter';

import costCalculationExternalsSettingsSchema from './cost-calculation-external-settings-schema';

const schema = costCalculationExternalsSettingsSchema();
type Form = InferType<typeof schema>;

export type AddEditCostCalculationExternalSettingsProperties = {
  employmentId?: number;
  organizationalUnitId?: number;
  isOpen: boolean;
  setting: CostCalculationExternalSurchargeSetting | null;
  handleClose: () => void;
};

export function AddEditCostCalculationExternalSetting({
  employmentId,
  organizationalUnitId,
  isOpen,
  setting,
  handleClose
}: AddEditCostCalculationExternalSettingsProperties) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const defaultValues = {
    validFrom: dayjs(setting?.validFrom),
    validTo: dayjs(setting?.validTo || dayjs().startOf('year').add(1, 'year')),
    amount: setting?.amount,
    fee: setting?.fee ? setting.fee * 100 : undefined,
    vatType: setting?.vatType,
    functionId: setting?.functionId,
    calculateFunctionType: setting?.calculateFunctionType || 'EMPLOYEE'
  };

  const { control, handleSubmit, reset, watch } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const vatTypeOptions: Option[] = [
    {
      value: 'NIL',
      label: t('noVat')
    },
    {
      value: 'HIGH',
      label: t('highVat')
    },
    {
      value: 'LOW',
      label: t('lowVat')
    }
  ];

  const validFrom = watch('validFrom');
  const validTo = watch('validTo');

  useEffect(() => {
    reset(defaultValues);
  }, [setting]);

  const {
    mutate: createCostCalculationExternalSetting,
    isLoading: isCreateLoading
  } = mutations.useCreateCostCalculationExternalSurchargeSettings({
    onError: (e: any) => {
      showSnackbar({
        severity: 'error',
        message: t(e?.response?.data?.error?.message || 'failed')
      });
    },
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries(
        queryKeys.getCostCalculationExternalSurchargeSettings(
          employmentId,
          organizationalUnitId
        )
      );
      showSnackbar({
        severity: 'success',
        message: t('addEditCostCalculationSettingSuccess')
      });
    }
  });

  const {
    mutate: updateCostCalculationExternalSetting,
    isLoading: isUpdateLoading
  } = mutations.useUpdateCostCalculationExternalSurchargeSettings({
    onError: (e: any) => {
      showSnackbar({
        severity: 'error',
        message: t(e?.response?.data?.error?.message || 'failed')
      });
    },
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries(
        queryKeys.getCostCalculationExternalSurchargeSettings(
          employmentId,
          organizationalUnitId
        )
      );
      showSnackbar({
        severity: 'success',
        message: t('addEditCostCalculationSettingSuccess')
      });
    }
  });

  const onSubmit = async (data: Form) => {
    if (setting) {
      updateCostCalculationExternalSetting({
        id: setting.id,
        employmentId,
        organizationalUnitId,
        validFrom: data.validFrom.format('YYYY-MM-DD'),
        validTo: data.validTo.format('YYYY-MM-DD'),
        amount: data.amount,
        functionId: data.functionId || undefined,
        calculateFunctionType: data.calculateFunctionType || undefined,
        vatType: data.vatType,
        fee: data.fee ? data.fee / 100 : undefined
      });
    } else {
      createCostCalculationExternalSetting({
        employmentId,
        organizationalUnitId,
        validFrom: data.validFrom.format('YYYY-MM-DD'),
        validTo: data.validTo.format('YYYY-MM-DD'),
        amount: data.amount,
        functionId: data.functionId || undefined,
        calculateFunctionType: data.calculateFunctionType || undefined,
        vatType: data.vatType,
        fee: data.fee ? data.fee / 100 : undefined
      } as CostCalculationExternalSurchargeSetting);
    }
  };

  return (
    <>
      <Drawer
        open={isOpen}
        title={t(
          setting ? 'editCostCalculationSetting' : 'addCostCalculationSetting'
        )}
        onClose={handleClose}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            mt={2}
            ml={2}
            mr={2}
          >
            <Box>
              <ValidFromValidTo control={control} />
            </Box>
            {organizationalUnitId && (
              <Box>
                <ControlledFunctionSelector
                  control={control}
                  name="functionId"
                  label={t('function')}
                  startDate={validFrom}
                  endDate={validTo}
                  onlyPreferred
                  organizationalUnitId={organizationalUnitId}
                  onlySelectable
                  fullWidth
                />
              </Box>
            )}
            {organizationalUnitId && (
              <Box ml={2} mr={2}>
                <ControlledRadioGroup
                  row
                  control={control}
                  name="calculateFunctionType"
                  label={t('calculateFunctionType')}
                  direction="row"
                  options={[
                    {
                      label: t('calculateFunctionTypeValue.EMPLOYEE'),
                      value: 'EMPLOYEE'
                    },
                    {
                      label: t('calculateFunctionTypeValue.DUTY'),
                      value: 'DUTY'
                    }
                  ]}
                />
              </Box>
            )}
            <Box>
              <ControlledMonetaryField
                control={control}
                name="amount"
                label={t('baseAmount')}
              />
            </Box>
            <Box>
              <ControlledPercentageField
                control={control}
                name="fee"
                label={t('fee')}
              />
            </Box>
            <Box>
              <ControlledSelect
                control={control}
                label={t('vatType')}
                name="vatType"
                options={vatTypeOptions}
              />
            </Box>
          </Box>
          <DrawerFooter>
            <Button onClick={handleClose} color="secondary" variant="contained">
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isCreateLoading || isUpdateLoading}
              type="submit"
              color="primary"
              variant="contained"
            >
              {t('save')}
            </LoadingButton>
          </DrawerFooter>
        </form>
      </Drawer>
    </>
  );
}
